// Facility-community management page (S2.2) — a NEW top-level /community route in
// the Operations nav group. ONE page, TWO states for the currently-selected
// facility (useResolvedFacility):
//   - no community yet (reader data === null) -> a create form
//     (name / description / visibility), gated on `facility:community`.
//   - community exists (CommunityResponse)    -> a manage view
//     (name / description / visibility / member_count) + an edit panel, gated on
//     `community:manage`.
//
// It is a THIN presentation layer: authorization is server-authoritative (the
// permission hooks only show/enable a control; the backend re-enforces every
// mutation), member counts come straight off the field (never fabricated), and a
// raw created_by Firebase UID is never rendered. The reader is TRI-STATE — null is
// the real empty state (a create CTA, NOT an error banner); a rejected read is the
// error state; an object is ready.
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { CommunityCreateForm } from "./community-create-form";
import { CommunityManageView } from "./community-manage-view";
import {
  useFacilityCommunity,
  useFacilityPermission,
} from "./use-facility-community";

export function CommunityPage() {
  const { facilityId, isLoading: facilityLoading } = useResolvedFacility();
  const community = useFacilityCommunity(facilityId);

  // Both permission checks run unconditionally (rules of hooks). Each consults the
  // SERVER for the action; the FE never derives a permission locally. An empty id
  // (no facility resolved yet) disables the underlying fetch and reads as
  // not-allowed (fail-closed) — but the page shows its loading skeleton in that
  // window anyway, so no control flashes.
  const { allowed: canCreate } = useFacilityPermission(
    facilityId ?? "",
    "facility:community",
  );
  const { allowed: canManage } = useFacilityPermission(
    facilityId ?? "",
    "community:manage",
  );
  // facility:community ALSO gates posting-as-facility + moderating the feed (S2.4)
  // — same server-authoritative grant as create. The FE gate only shows/enables a
  // control; the backend re-enforces every post / removal.
  const canCommunity = canCreate;

  // Body content, computed with narrowing if-statements (so `facilityId` is a
  // confirmed string in the create-form branch — the `!facilityId` loading guard
  // runs first).
  let body: ReactNode;
  if (facilityLoading || !facilityId || community.isLoading) {
    body = (
      <Stack aria-label="Loading community" role="status" spacing={1}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton height={48} key={i} variant="rounded" width="100%" />
        ))}
      </Stack>
    );
  } else if (community.isError) {
    body = (
      <Alert severity="error">
        {community.error instanceof Error
          ? community.error.message
          : "Failed to load community."}
      </Alert>
    );
  } else if (community.data) {
    // key on the community identity: switching the selected facility A→B (or any
    // community-identity change) REMOUNTS the manage view so its edit-panel state
    // re-seeds and an open panel closes. Without this, an open edit panel keeps
    // Facility A's values and Save would PATCH them onto B's community (wrong-target
    // lost update + private→public regression + phantom success).
    body = (
      <CommunityManageView
        key={community.data.community_id}
        community={community.data}
        facilityId={facilityId}
        canManage={canManage}
        canPost={canCommunity}
        canModerate={canCommunity}
      />
    );
  } else {
    // data === null — the real "no community yet" empty state. A create CTA,
    // never an error banner.
    body = (
      <Paper
        component="section"
        sx={{ p: { xs: 2, sm: 2.5 } }}
        variant="outlined"
      >
        <Stack spacing={2}>
          <Typography component="h2" variant="h6">
            Create your facility&apos;s community
          </Typography>
          <CommunityCreateForm
            key={facilityId}
            facilityId={facilityId}
            canCreate={canCreate}
            onSettled={() => community.refetch()}
          />
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 }, pb: 2 }}>
      <Box>
        <Typography component="h1" sx={{ fontWeight: 700 }} variant="h5">
          Community
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Your facility's community hub for players.
        </Typography>
      </Box>
      {body}
    </Stack>
  );
}
